import {IEventEmitter, TypesCore} from "@dp-instant/interfaces";
import {Localize} from "@dp-instant/i18n";
import {WaitAction} from "@dp-instant/core";
import {AdResponse} from "@dp-instant/interfaces";
import {UIEvents, useInjection, visibilityEffect} from "@dp-instant/ui";
import {GameModel} from "@game/coregame/model/GameModel";
import {showAd, showRewardOrPay} from "@game/helpers";
import {TypesGame} from "@game/TypesGame";
import {ClaimButton} from "@game/ui/components/ClaimButton/ClaimButton";
import {CoinsIndicator} from "@game/ui/components/CoinsIndicator/CoinsIndicator";
import {PopupWinIndicator} from "@game/ui/components/CoinsIndicator/PopupWinIndicator";
import {ContinueButton} from "@game/ui/components/ContinueButton/ContinueButton";
import {NoThanksButton} from "@game/ui/components/NoThanksButton/NoThanksButton";
import {WinRays} from "@game/ui/components/WinRays/WinRays";
import {WinStars} from "@game/ui/components/WinStars/WinStars";
import {UIConstants} from "@game/ui/constants";
import cn from "classnames";
import {useEffect, useState} from "preact/hooks";
import {MultiplyBonus} from "../components/MultiplyBonus/MultiplyBonus";
import {SVG} from "../images";
import "./OfflineEarninfsPopup.less";

type OfflineEarningsProps = {
    animationEnabled?: boolean;
    coins?: number;
    hours?: number;
    minutes?: number;
    seconds?: number;
    days?: number;
};

const formatTime = (unitOfTime: number): string => {
    return unitOfTime >= 10 ? `${unitOfTime}` : `0${unitOfTime}`;
};

export const OfflineEarningsPopup = ({
    coins = 1000,
    hours = 23,
    minutes = 59,
    seconds = 59,
    animationEnabled = true,
    days = 0
}: OfflineEarningsProps) => {
    const model = useInjection<GameModel>(TypesGame.model);
    const dispatcher: IEventEmitter = useInjection(TypesCore.dispatcher);
    const initialState = {
        startCoinsTotal: model.cookie.coins,
        adViewed: false,
        multiplierSelected: false,
        mult: 1,
        reward: coins
    };
    const [state, setState] = useState(() => initialState);
    const [invisible] = visibilityEffect(UIConstants.popup.startDelay, false, [state.adViewed]);
    const indicatorUpdateDelay = UIConstants.coinsIndicator.updateDelay;

    useEffect(() => {
        if (state.adViewed && state.multiplierSelected && state.mult > 1) {
            setState(state => ({...state, reward: state.reward * state.mult}));
        }
    }, [state.multiplierSelected, state.adViewed, state.mult]);

    const close = async () => {
        updateCoinsModel(state.reward);
        await WaitAction.ms(indicatorUpdateDelay);
        dispatcher.emit(UIEvents.POPUP, {id: null});
    };

    const onClaim = async () => {
        setState(state => ({...state, multiplierSelected: true}));
        const results = await showRewardOrPay(model.cookie.getVip, "reward_multiply");
        if (results) {
            setState(state => ({...state, adViewed: true}));
        } else {
            setState(state => ({...state, multiplierSelected: false}));
        }
    };

    const onDecline = async () => {
        if (__SOCIAL__ !== "tg") {
            await showAd();
        }
        onContinue();
    };

    const updateCoinsModel = (coins: number) => {
        model.cookie.coins += coins;
    };

    const onPause = (mult: number) => {
        setState(state => ({...state, mult: mult}));
    };

    const onContinue = async () => {
        model.cookie.syncTime();
        await close();
    };

    return (
        <div className="popups">
            <div className={cn("coins-bar")}>
                <CoinsIndicator
                    className={cn("coins-indicator", "coins-indicator_filled")}
                    total={state.startCoinsTotal}
                />
            </div>

            <div className={cn("popup-offline-earnings")}>
                <div className={cn("popup", {invisible})}>
                    <div className="popup__title">
                        {Localize.get("ui-offline-title", "OFFLINE EARNINGS")}
                    </div>
                    {animationEnabled ? (
                        <div className="popup__main-animation">
                            <SVG.OfflineEarnings />
                            <WinRays />
                            <WinStars />
                        </div>
                    ) : null}

                    <div className="popup__body">
                        <div className={cn("popup__body-line")}>
                            {Localize.get("ui-offline-description", "YOU WERE AWAY FOR:")}
                        </div>

                        <div className={cn("popup__body-line", "time-away")}>
                            {__SOCIAL__ === "yt" || __SOCIAL__ === "ls" ? (
                                <>
                                    {days ? <span>{formatTime(days)}D:</span> : null}
                                    <span>{formatTime(hours)}H:</span>
                                    <span>{formatTime(minutes)}M:</span>
                                    <span>{formatTime(seconds)}S</span>
                                </>
                            ) : (
                                <>
                                    <span>{hours}H:</span>
                                    <span>{minutes}M:</span>
                                    <span>{seconds}S</span>
                                </>
                            )}
                        </div>

                        <PopupWinIndicator
                            total={state.reward}
                            className={cn(
                                "coins-indicator",
                                "coins-indicator_internal",
                                "coins-indicator_filled"
                            )}
                        />

                        {model.rewardedAdsAllowed ? (
                            <div className={cn("popup__body-line")}>
                                <MultiplyBonus
                                    paused={state.multiplierSelected}
                                    onPause={onPause}
                                />
                            </div>
                        ) : null}
                    </div>

                    {!state.adViewed && model.rewardedAdsAllowed ? (
                        <div className={"popup__body-buttons"}>
                            <div className={cn("popup__body-button", "center")}>
                                {!state.adViewed ? (
                                    <ClaimButton
                                        onClick={onClaim}
                                        text={Localize.get("ui-offline-mult_button", "MULTIPLY")}
                                        starsPrice={5}
                                    />
                                ) : null}
                            </div>
                        </div>
                    ) : null}

                    {state.adViewed ? (
                        <ContinueButton onClick={onContinue} />
                    ) : (
                        <NoThanksButton
                            delay={UIConstants.popup.noThanksButtonDelay}
                            onClick={onDecline}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};
